import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, LoaderCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = ["General question", "Support", "Feedback", "Partnership", "Press"];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent!", { description: "We'll get back to you as soon as possible." });
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Something went wrong", { description: "Please check your email address and try again." });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/15";

  return (
    <section id="support" className="bg-[#F0F5F2] py-20 lg:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="overline-label">Support</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Questions?<br />We're <span className="font-serif-accent italic text-[#2E7D5B]">listening.</span>
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-slate-600">
            Whether it's a question about GoDiGo, feedback on the app or anything else — drop us a message and the team will reply.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-3xl border border-slate-200 bg-white p-8"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
            <input data-testid="contact-email-input" className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
          </div>
          <select data-testid="contact-topic-select" className={`${inputCls} mt-4`} value={form.topic} onChange={set("topic")}>
            {TOPICS.map((t) => <option key={t}>{t}</option>)}
          </select>
          <textarea data-testid="contact-message-input" className={`${inputCls} mt-4 min-h-[130px] resize-none`} placeholder="How can we help?" value={form.message} onChange={set("message")} />
          <button data-testid="contact-submit-button" type="submit" disabled={sending} className="btn-primary mt-5 w-full px-6 py-3.5 text-sm disabled:opacity-60">
            {sending ? <LoaderCircle size={16} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Sending…" : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
